import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Search, Wallet, ShieldCheck, Ban, FileText } from 'lucide-react';
import { toast } from '@/lib/toast';
import UserDetailsDrawer from './UserDetailsDrawer';

const formatInr = (v) =>
    `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Round 40m — currency-aware wallet display in the admin grid.
// `wallet_balance_inr` is the smallest-unit balance divided by 100 —
// for INR users that's rupees; for USD users it's dollars. We pick
// the symbol from the user's persisted residence_country.
const formatWallet = (u) => {
    const v = Number(u?.wallet_balance_inr ?? 0);
    const cc = (u?.residence_country || '').toUpperCase();
    const isUsd = cc && cc !== 'IN' && cc !== 'IND';
    return isUsd
        ? `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Round 40m — compact country cell. Renders the 2-letter ISO code
// with a flag emoji when available, '—' when the user hasn't paid
// or been geo-detected yet.
const COUNTRY_FLAGS = {
    IN: '🇮🇳', US: '🇺🇸', GB: '🇬🇧', CA: '🇨🇦', AU: '🇦🇺',
    DE: '🇩🇪', FR: '🇫🇷', SG: '🇸🇬', AE: '🇦🇪', SA: '🇸🇦',
    JP: '🇯🇵', NZ: '🇳🇿', NL: '🇳🇱', CH: '🇨🇭', IE: '🇮🇪',
};
function CountryCell({ code }) {
    if (!code) return <span className="text-slate-600">—</span>;
    const cc = String(code).toUpperCase();
    const flag = COUNTRY_FLAGS[cc] || '';
    return (
        <span className="inline-flex items-center gap-1.5 text-xs">
            {flag && <span aria-hidden>{flag}</span>}
            <span className="font-mono">{cc}</span>
        </span>
    );
}

// Round 40o — phone is stored as E.164 (`+919876543210`) on the user
// document. Show the full number with the dialling country code prefix.
// Falls back to '—' when the user signed up before the phone-capture
// rollout or via Google OAuth (which doesn't yet capture phone).
function ContactCell({ phoneE164, phoneCountry }) {
    const p = (phoneE164 || '').trim();
    if (!p) return <span className="text-slate-600 text-xs">—</span>;
    const cc = (phoneCountry || '').toUpperCase();
    const flag = COUNTRY_FLAGS[cc] || '';
    return (
        <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
            {flag && <span aria-hidden>{flag}</span>}
            <span className="font-mono">{p}</span>
        </span>
    );
}

export default function AdminUsers() {
    const [q, setQ] = useState('');
    const [data, setData] = useState({ rows: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [walletDelta, setWalletDelta] = useState('');
    const [walletDeltaNote, setWalletDeltaNote] = useState('');
    const [detailsFor, setDetailsFor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/users', { params: { q: q || undefined, limit: 50 } });
            setData(data);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const patch = async (id, body) => {
        try {
            const { data: u } = await api.patch(`/admin/users/${id}`, body);
            setData(d => ({ ...d, rows: d.rows.map(r => r.id === id ? u : r) }));
            if (selected?.id === id) setSelected(u);
            toast.success('User updated');
        } catch (e) {
            toast.error(readErr(e, 'Failed'));
        }
    };

    const applyWalletDelta = async () => {
        const n = parseFloat(walletDelta);
        if (!Number.isFinite(n) || n === 0) return toast.error('Enter a non-zero ₹ amount (use a minus sign to debit)');
        await patch(selected.id, {
            wallet_delta_inr: n,
            wallet_delta_note: walletDeltaNote || undefined,
        });
        setWalletDelta('');
        setWalletDeltaNote('');
    };

    return (
        <div className="p-6 lg:p-10 space-y-6" data-testid="admin-users">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Users</div>
                    <h1 className="font-serif text-3xl text-parchment mt-2">{data.total} accounts</h1>
                </div>
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search email or name…"
                        data-testid="users-search"
                        className="pl-9 w-72 bg-elevated border-marigold/15 text-parchment rounded-sm"
                    />
                </div>
            </div>

            <Card className="rounded-sm bg-elevated border-marigold/10 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-slate-500 text-xs uppercase tracking-wider border-b border-marigold/10">
                            <tr>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Contact</th>
                                <th className="px-4 py-3">Country</th>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Wallet</th>
                                <th className="px-4 py-3">Flags</th>
                                <th className="px-4 py-3">Joined</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="8" className="px-4 py-6 text-slate-500">Loading…</td></tr>}
                            {!loading && data.rows.length === 0 && (
                                <tr><td colSpan="8" className="px-4 py-6 text-slate-500" data-testid="users-empty">No users found.</td></tr>
                            )}
                            {data.rows.map(u => (
                                <tr key={u.id} className="border-b border-marigold/5 hover:bg-obsidian/40" data-testid={`user-row-${u.id}`}>
                                    <td className="px-4 py-3 text-parchment">{u.email}</td>
                                    <td className="px-4 py-3" data-testid={`user-contact-${u.id}`}>
                                        <ContactCell phoneE164={u.phone_e164} phoneCountry={u.phone_country} />
                                    </td>
                                    <td className="px-4 py-3 text-slate-300" data-testid={`user-country-${u.id}`}>
                                        <CountryCell code={u.residence_country} />
                                    </td>
                                    <td className="px-4 py-3 text-slate-300">{u.name}</td>
                                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-marigold" data-testid={`user-wallet-${u.id}`}><Wallet size={12} />{formatWallet(u)}</span></td>
                                    <td className="px-4 py-3 space-x-1">
                                        {u.is_admin && <Badge className="bg-marigold/15 text-marigold border-marigold/30 hover:bg-marigold/15">admin</Badge>}
                                        {u.banned && <Badge className="bg-rose-500/15 text-rose-400 border-rose-500/30">banned</Badge>}
                                        {u.birth_details?.date && <Badge className="bg-elevated text-slate-400 border-marigold/15">onboarded</Badge>}
                                        {u.birth_fingerprint_duplicate && (
                                            <Badge
                                                className="bg-rose-500/15 text-rose-400 border-rose-500/30"
                                                title="Birth details match an earlier account — signup bonus reclaimed"
                                                data-testid={`user-duplicate-flag-${u.id}`}
                                            >
                                                duplicate birth
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-500 text-xs">{u.created_at?.slice(0, 10)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                size="sm" variant="outline"
                                                onClick={() => setDetailsFor(u)}
                                                data-testid={`user-details-${u.id}`}
                                                className="bg-transparent border-marigold/30 text-parchment hover:bg-marigold/10 rounded-sm h-8 px-3 text-xs"
                                            >
                                                <FileText size={12} className="mr-1.5" />
                                                Details
                                            </Button>
                                            <Button
                                                size="sm" variant="outline"
                                                onClick={() => setSelected(u)}
                                                data-testid={`user-manage-${u.id}`}
                                                className="bg-transparent border-marigold/30 text-parchment hover:bg-marigold/10 rounded-sm h-8 px-3 text-xs"
                                            >Manage</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="bg-surface border-marigold/15 rounded-sm max-w-md" data-testid="user-manage-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-parchment">Manage user</DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-5">
                            <div>
                                <div className="text-sm text-parchment">{selected.email}</div>
                                <div className="text-xs text-slate-500">{selected.name} · Wallet {formatInr(selected.wallet_balance_inr)}</div>
                            </div>

                            <div>
                                <Label className="text-xs text-slate-400 uppercase tracking-wider">Adjust wallet (₹ delta — signed)</Label>
                                <div className="flex gap-2 mt-2">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={walletDelta}
                                        onChange={(e) => setWalletDelta(e.target.value)}
                                        placeholder="e.g. 100 to credit, -50 to debit"
                                        data-testid="wallet-delta-input"
                                        className="bg-obsidian border-marigold/20 text-parchment rounded-sm"
                                    />
                                    <Button
                                        onClick={applyWalletDelta}
                                        data-testid="wallet-apply"
                                        className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-sm"
                                    >Apply</Button>
                                </div>
                                <Input
                                    type="text"
                                    value={walletDeltaNote}
                                    onChange={(e) => setWalletDeltaNote(e.target.value)}
                                    placeholder="Optional note (logged in the ledger)"
                                    data-testid="wallet-delta-note"
                                    className="mt-2 bg-obsidian border-marigold/20 text-parchment rounded-sm text-xs"
                                />
                                <div className="mt-2 text-[11px] text-slate-500">
                                    Posts an audited <code className="text-marigold/80">admin_adjust</code> row to the wallet ledger.
                                    Positive credits the user; negative debits (refused if it would overdraft).
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => patch(selected.id, { is_admin: !selected.is_admin })}
                                    data-testid="toggle-admin"
                                    className="bg-transparent border-marigold/30 text-parchment hover:bg-marigold/10 rounded-sm h-10"
                                >
                                    <ShieldCheck size={14} className="mr-2" />
                                    {selected.is_admin ? 'Revoke admin' : 'Make admin'}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => patch(selected.id, { banned: !selected.banned })}
                                    data-testid="toggle-ban"
                                    className={`bg-transparent rounded-sm h-10 ${selected.banned
                                        ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                                        : 'border-rose-500/30 text-rose-400 hover:bg-rose-500/10'}`}
                                >
                                    <Ban size={14} className="mr-2" />
                                    {selected.banned ? 'Unban' : 'Ban'}
                                </Button>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelected(null)} className="bg-transparent border-marigold/20 text-slate-300 rounded-sm">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <UserDetailsDrawer
                user={detailsFor}
                open={!!detailsFor}
                onOpenChange={(o) => !o && setDetailsFor(null)}
            />
        </div>
    );
}
